import { useState } from 'react'

import { useI18n } from '@muse/i18n'
import { useNavigate } from '@muse-web/router'

import { sendEvent } from '@core/analytics'
import { type TAudioSimple } from '@core/models'
import {
    AddToListIcon,
    DotListIcon,
    DownloadIcon,
    IconButton,
    PencilIcon,
    ReplaceIcon,
    ShareIcon,
    toast,
    TrashBinIcon,
} from '@ds/components'

import { getAudioEditPageUrl } from '@features/audio'
import {
    AsyncDeleteAudioDialog,
    ReplaceSourceInput,
    useDeleteAudio,
    useReplaceAudioSource,
} from '@features/audio-edit'
import { AsyncChangeTrackOrderDialogContainer, type TOrderableList } from '@features/collection'
import { AudioCardActionsMenu, type TAudioCardMenuItem } from '../components/audio-card'

type TAudioCardOwnerActionsContainerProps = {
    audio: TAudioSimple
    canDownload: boolean
    orderableList?: TOrderableList
    onShare: () => void
    onAddToCollection: () => void
    onDownload: () => void
}

/**
 * Split out of {@see AudioCardContainer} so the replace and delete machinery is only mounted for
 * cards the user actually owns — a browse page holds twenty of these.
 */
export const AudioCardOwnerActionsContainer = ({
    audio,
    canDownload,
    orderableList,
    onShare,
    onAddToCollection,
    onDownload,
}: TAudioCardOwnerActionsContainerProps) => {
    const { t } = useI18n()
    const navigate = useNavigate()
    const deleteAudio = useDeleteAudio()
    const [isDeleteOpen, setIsDeleteOpen] = useState(false)
    const [isChangeOrderOpen, setIsChangeOrderOpen] = useState(false)

    const showReplaceError = () => {
        toast.error(t('i18n.audioCardReplaceError'))
    }

    const replaceSource = useReplaceAudioSource({
        audioId: audio.id,
        authorUsername: audio.authorUsername,
        onError: showReplaceError,
        onProcessingTimeout: showReplaceError,
    })

    const handleEdit = () => {
        sendEvent({ name: 'audio_edit_click', payload: { audio: audio.id } })
        navigate(
            getAudioEditPageUrl({
                authorUsername: audio.authorUsername,
                audioSlug: audio.slug,
            }),
        )
    }

    const handleDeleteRequest = () => {
        setIsDeleteOpen(true)
    }

    const handleChangeOrderRequest = () => {
        setIsChangeOrderOpen(true)
    }

    const handleChangeOrderClose = () => {
        setIsChangeOrderOpen(false)
    }

    const confirmDelete = async () => {
        try {
            await deleteAudio.mutateAsync({
                audioId: audio.id,
                audioSlug: audio.slug,
                authorUsername: audio.authorUsername,
            })
            setIsDeleteOpen(false)
        } catch {
            toast.error(t('i18n.audioDeleteError'))
        }
    }

    // An owner is signed in by definition, so no auth branch here.
    const menuItems: TAudioCardMenuItem[] = [
        {
            id: 'replace-source',
            label: t('i18n.audioCardReplaceSource'),
            Icon: ReplaceIcon,
            onSelect: replaceSource.openFilePicker,
        },
        {
            id: 'add-to-collection',
            label: t('i18n.audioCardAddToCollection'),
            Icon: AddToListIcon,
            onSelect: onAddToCollection,
        },
        ...(canDownload
            ? [
                  {
                      id: 'download',
                      label: t('i18n.audioCardDownload'),
                      Icon: DownloadIcon,
                      onSelect: onDownload,
                  },
              ]
            : []),
        ...(orderableList
            ? [
                  {
                      id: 'update-order-position',
                      label: t('i18n.audioCardChangeOrder'),
                      Icon: DotListIcon,
                      onSelect: handleChangeOrderRequest,
                  },
              ]
            : []),
        {
            id: 'delete',
            label: t('i18n.commonDelete'),
            Icon: TrashBinIcon,
            onSelect: handleDeleteRequest,
        },
    ]

    return (
        <>
            <IconButton
                variant="outline"
                Icon={PencilIcon}
                aria-label={t('i18n.commonEdit')}
                onClick={handleEdit}
            />
            <IconButton
                variant="outline"
                Icon={ShareIcon}
                aria-label={t('i18n.audioCardShare')}
                onClick={onShare}
            />
            <AudioCardActionsMenu
                items={menuItems}
                triggerLabel={t('i18n.audioCardMoreActions')}
                sheetTitle={t('i18n.audioActionsSheetTitle')}
                cancelLabel={t('i18n.commonCancel')}
                onTriggerIntent={AsyncDeleteAudioDialog.preload}
            />
            {/* Must live outside the menu: it unmounts on select, killing the change event. */}
            <ReplaceSourceInput
                ref={replaceSource.fileInputRef}
                onSelectFile={replaceSource.replaceSource}
            />
            <AsyncDeleteAudioDialog
                open={isDeleteOpen}
                onOpenChange={setIsDeleteOpen}
                onConfirm={confirmDelete}
                isDeleting={deleteAudio.isPending}
                title={t('i18n.audioDeleteDialogTitle')}
                description={audio.title}
                confirmLabel={t('i18n.commonDelete')}
                deletingLabel={t('i18n.commonDeleting')}
                cancelLabel={t('i18n.commonCancel')}
            />
            {orderableList && isChangeOrderOpen ? (
                <AsyncChangeTrackOrderDialogContainer
                    audioId={audio.id}
                    list={orderableList}
                    onClose={handleChangeOrderClose}
                />
            ) : null}
        </>
    )
}
